import { useEffect, useRef } from 'react';
import { setTurnstileToken, clearTurnstileToken, registerTurnstileRefresher } from '@services/turnstile';

interface TurnstileProps {
  onTokenReady?: () => void;
  // Fired when a previously-issued token expires or the widget errors, so callers can re-disable
  // any action gated on a valid token (otherwise the user clicks after expiry and the request
  // fails captcha).
  onInvalidated?: () => void;
}

export default function Turnstile({ onTokenReady, onInvalidated }: TurnstileProps) {
  const ref = useRef<HTMLDivElement>(null);
  const widgetIdRef = useRef<string | undefined>(undefined);
  const siteKey = (import.meta.env.VITE_TURNSTILE_SITE_KEY as string | undefined) ?? '';

  // Keep stable references to the latest callbacks so the effect
  // does not need to re-run (and recreate the widget) when they change.
  const onTokenReadyRef = useRef(onTokenReady);
  const onInvalidatedRef = useRef(onInvalidated);
  useEffect(() => {
    onTokenReadyRef.current = onTokenReady;
    onInvalidatedRef.current = onInvalidated;
  }, [onTokenReady, onInvalidated]);

  useEffect(() => {
    if (!siteKey) return;
    let mounted = true;

    const ensure = () => {
      if (!mounted || !ref.current) return;

      if (typeof window === 'undefined' || !window.turnstile?.render) {
        window.setTimeout(ensure, 100);
        return;
      }

      // Clear previous markup in this container to avoid stacking multiple widgets
      ref.current.innerHTML = '';

      widgetIdRef.current = window.turnstile.render(ref.current, {
        sitekey: siteKey,
        theme: 'light',
        callback: (token: string) => {
          if (!mounted) return;
          setTurnstileToken(token);
          // Notify on every issued token (including a refreshed one after an expiry) so gated
          // actions re-enable as soon as a fresh, valid token is available.
          onTokenReadyRef.current?.();
        },
        'error-callback': () => {
          clearTurnstileToken();
          if (mounted) onInvalidatedRef.current?.();
        },
        'expired-callback': () => {
          clearTurnstileToken();
          if (mounted) onInvalidatedRef.current?.();
        },
        retry: 'auto',
      });

      // Let API-level code force a fresh challenge when the backend rejects a stale/consumed token
      // mid-flow (see refreshTurnstileToken). Reset re-runs the widget, which re-invokes callback.
      registerTurnstileRefresher(() => {
        if (widgetIdRef.current) {
          window.turnstile?.reset?.(widgetIdRef.current);
        }
      });
    };

    // Load API script once
    const id = 'cf-turnstile-api';
    if (typeof document !== 'undefined' && !document.getElementById(id)) {
      const s = document.createElement('script');
      s.id = id;
      s.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
      s.async = true;
      s.defer = true;
      document.head.appendChild(s);
    }

    ensure();
    return () => {
      mounted = false;
      registerTurnstileRefresher(null);
      if (widgetIdRef.current) {
        try {
          window.turnstile?.reset?.(widgetIdRef.current);
        } catch {
          // ignore reset errors
        }
        widgetIdRef.current = undefined;
      }
    };
  }, [siteKey]);

  if (!siteKey) return null;
  return <div ref={ref} className="cf-turnstile" />;
}
